import { readOutbox } from './db';

/**
 * Emendas: o que está na fila, aplicado por cima do que veio do servidor.
 *
 * Sem isto, salvar offline funcionava mas não aparecia na tela. A escrita
 * entrava na fila corretamente, e logo depois a tela recarregava a lista — que
 * vinha da cópia local de ANTES da inserção. Para quem estava usando, parecia
 * que o lançamento tinha sumido.
 *
 * A emenda viaja junto do item na fila. Isso resolve a limpeza sozinho: quando
 * o item sobe, ele é removido, e a emenda desaparece com ele. Não existe estado
 * paralelo para sincronizar nem para esquecer de apagar.
 *
 * Vale online também: se a escrita foi para o servidor, não há item na fila e
 * não há emenda. Só sobra quando algo está mesmo pendente.
 */

const porOrdemDeChegada = (a, b) => (a.seq || 0) - (b.seq || 0);

/** Aplica uma emenda a uma lista. */
const aplicarUma = (lista, patch) => {
  const atual = Array.isArray(lista) ? lista : [];

  switch (patch.tipo) {
    case 'insere': {
      // Se já existe (o item subiu e a lista veio do servidor), não duplica.
      if (atual.some((x) => x.id === patch.linha.id)) return atual;
      return [...atual, patch.linha];
    }
    case 'atualiza':
      return atual.map((x) => (x.id === patch.id ? { ...x, ...patch.campos } : x));
    case 'remove':
      return atual.filter((x) => x.id !== patch.id);
    default:
      return atual;
  }
};

/** Ordena pelo mesmo campo que a consulta original usava, para o item novo não cair no fim. */
const ordenar = (lista, campo) => {
  if (!campo) return lista;
  return [...lista].sort((a, b) =>
    String(a?.[campo] ?? '').localeCompare(String(b?.[campo] ?? ''), 'pt-BR'));
};

/**
 * Aplica todas as emendas pendentes para uma chave de cache.
 *
 * Em ordem de `seq`: duas edições do mesmo registro precisam terminar no estado
 * da última, e é a mesma ordem em que a fila vai enviá-las.
 */
export async function aplicarPendentes(chave, dados) {
  const fila = await readOutbox();
  const relevantes = fila
    .filter((r) => r.patch?.chave === chave)
    .sort(porOrdemDeChegada);

  if (relevantes.length === 0) return dados;

  let resultado = dados;
  let ordenarPor = null;
  for (const registro of relevantes) {
    resultado = aplicarUma(resultado, registro.patch);
    ordenarPor = registro.patch.ordenarPor || ordenarPor;
  }
  return ordenar(resultado, ordenarPor);
}
